import { useState } from "react";
import { Box, Flex, Image, Text } from "rebass/styled-components";
import styled from "styled-components";
import { useRecoilValue } from "recoil";

import { formatRelativeTime } from "MVVM/Utilities/Time";
import {
  ActivePlaylistClipState,
  PlaylistClipsState,
} from "MVVM/State/embeddablePlaylist";
import { LoadingSkeleton } from "MVVM/Components/Skeloton";
import { PlaylistClip } from "../../@types";
import { getMetadataValue } from "../../Utilities";

export const LeaguePlayerInfo = ({ isOPGG = false }: { isOPGG?: boolean }) => {
  const [summonerImageLoaded, setSummonerImageLoaded] = useState(false);
  const [championImageLoaded, setChampionImageLoaded] = useState(false);

  const activePlaylistClip = useRecoilValue(ActivePlaylistClipState);
  const playlistClips = useRecoilValue(PlaylistClipsState);

  const currentClip =
    playlistClips?.find(
      (clip: PlaylistClip) => clip._id === activePlaylistClip?.id,
    ) || playlistClips?.[0];

  const username = activePlaylistClip?.username || "";
  const [summonerName, region] = username.includes("#")
    ? username.split("#")
    : [username, ""];

  const profileIcon =
    getMetadataValue(currentClip?.metadata, "Profile Icon") || "1";

  const championNameExceptions: Record<string, string> = {
    "Kog'Maw": "KogMaw",
    LeBlanc: "Leblanc",
    Wukong: "MonkeyKing",
    "Nunu & Willump": "Nunu",
    "Rek'Sai": "RekSai",
  };
  const championRaw = getMetadataValue(currentClip?.metadata, "Champion");
  const champion =
    championNameExceptions[championRaw] ||
    championRaw
      .replace(/'([A-Z])/g, (_, letter) => letter.toLowerCase())
      .replace(/[^a-zA-Z0-9]/g, "");

  const matchKills = getMetadataValue(currentClip?.metadata, "Match Kills");
  const matchDeaths = getMetadataValue(currentClip?.metadata, "Match Deaths");
  const matchAssists = getMetadataValue(currentClip?.metadata, "Match Assists");
  const summonerSpell1Id = getMetadataValue(
    currentClip?.metadata,
    "Summoner Spell 1 ID",
  );
  const summonerSpell2Id = getMetadataValue(
    currentClip?.metadata,
    "Summoner Spell 2 ID",
  );

  // Sometimes these come back as 0 for some reason which doesn't map to any rune.
  // Just defaulting to a random perk here to keep the UI from looking bad in demo, but newer data should never expose this value.
  const perkKeystone =
    getMetadataValue(currentClip?.metadata, "Perk Keystone") === "0"
      ? "8008"
      : getMetadataValue(currentClip?.metadata, "Perk Keystone");
  const perkSecondaryStyle =
    getMetadataValue(currentClip?.metadata, "Perk Secondary Style") === "0"
      ? "8300"
      : getMetadataValue(currentClip?.metadata, "Perk Secondary Style");
  const matchType = getMetadataValue(
    currentClip?.metadata,
    "Match Type",
  ).replace(/ games?$/i, "");

  const gamePlayed = getMetadataValue(currentClip?.metadata, "Game Played");
  const matchDate = formatRelativeTime(
    gamePlayed || currentClip?.createdDate || "",
  );

  return (
    <Flex flexDirection="column" alignItems="center" style={{ gap: "12px" }}>
      <StyledSummonerContainer>
        {!summonerImageLoaded && (
          <StyledSummonerSkeleton>
            <LoadingSkeleton width="64px" height="64px" />
          </StyledSummonerSkeleton>
        )}
        <StyledSummonerImage
          src={`${process.env.REACT_APP_S3_CLIP_BUCKET}/static/web/lol/summoner_icons/${profileIcon}.png`}
          onLoad={() => setSummonerImageLoaded(true)}
          style={{ display: summonerImageLoaded ? "block" : "none" }}
          $isOPGG={isOPGG}
        />
        <Text
          variant="text.labelXSmall"
          fontWeight={700}
          style={{ padding: "12px 0 6px" }}
        >
          {summonerName}
        </Text>
        <StyledTaglineText>#{region}</StyledTaglineText>
      </StyledSummonerContainer>
      <StyledChampionContainer $isOPGG={isOPGG}>
        <Flex style={{ gap: "3px" }}>
          {!championImageLoaded && (
            <StyledChampionSkeleton>
              <LoadingSkeleton width="39px" height="39px" />
            </StyledChampionSkeleton>
          )}
          <StyledChampionImage
            src={`${process.env.REACT_APP_S3_CLIP_BUCKET}/static/web/lol/skins/tile/${champion}/Default/${champion}.webp`}
            onLoad={() => setChampionImageLoaded(true)}
            style={{ display: championImageLoaded ? "block" : "none" }}
          />
          <Flex style={{ gap: "3px" }}>
            <Flex flexDirection="column" style={{ gap: "3px" }}>
              <StyledMatchIcon
                src={`${process.env.REACT_APP_S3_CLIP_BUCKET}/static/web/lol/summoner_spells/${summonerSpell1Id}.png`}
              />
              <StyledMatchIcon
                src={`${process.env.REACT_APP_S3_CLIP_BUCKET}/static/web/lol/summoner_spells/${summonerSpell2Id}.png`}
              />
            </Flex>
            <Flex flexDirection="column" style={{ gap: "3px" }}>
              <StyledMatchIcon
                src={`${process.env.REACT_APP_S3_CLIP_BUCKET}/static/web/lol/runes/${perkKeystone}.png`}
              />
              <StyledMatchIcon
                src={`${process.env.REACT_APP_S3_CLIP_BUCKET}/static/web/lol/runes/${perkSecondaryStyle}.png`}
              />
            </Flex>
          </Flex>
        </Flex>
        <StyledMatchResults>
          {matchKills}/<span>{matchDeaths}</span>/{matchAssists}
        </StyledMatchResults>
        <StyledGameMode>{matchType}</StyledGameMode>
        <StyledMatchDate>{matchDate}</StyledMatchDate>
      </StyledChampionContainer>
    </Flex>
  );
};

const StyledSummonerContainer = styled(Flex)`
  align-items: center;
  flex-direction: column;
  justify-content: center;
`;

const StyledSummonerImage = styled(Image)<{ $isOPGG: boolean }>`
  border: ${({ $isOPGG }) =>
    $isOPGG ? "none" : "1px solid rgba(50, 115, 250, 1)"};
  border-radius: 6px;
  height: 64px;
  width: 64px;
`;

const StyledTaglineText = styled(Text)`
  font-weight: 700;
  font-size: 8px;
  line-height: 13px;
`;

const StyledChampionContainer = styled(Flex)<{ $isOPGG: boolean }>`
  background: rgba(50, 115, 250, 0.25);
  border-radius: 3px;
  flex-direction: column;
  padding: 11px;
  width: 100px;

  ${({ $isOPGG }) =>
    $isOPGG &&
    `
      border-left: 4px solid rgba(83, 131, 232, 1)
    `}
`;

const StyledChampionImage = styled(Image)`
  border-radius: 3px;
  height: 39px;
  width: 39px;
`;

const StyledMatchIcon = styled(Image)`
  border-radius: 3px;
  height: 18px;
  width: 18px;
`;

const StyledGameMode = styled(Text)`
  color: ${({ theme }) => theme.colors.white};
  font-size: 10px;
  font-weight: 700;
  margin: 2px 0;
`;

const StyledMatchDate = styled(Flex)`
  color: #cddcfe;
  font-size: 8px;
  font-weight: 500;
`;

const StyledMatchResults = styled(Flex)`
  color: ${({ theme }) => theme.colors.white};
  font-size: 10px;
  font-weight: 700;
  margin-top: 6px;
  letter-spacing: 1px;

  span {
    color: #ff4e50;
  }
`;

const StyledSummonerSkeleton = styled(Box)`
  border-radius: 6px;
  overflow: hidden;
  position: absolute;
`;

const StyledChampionSkeleton = styled(Box)`
  border-radius: 3px;
  overflow: hidden;
  position: absolute;
`;
